import { useCallback, useEffect, useRef, useState } from 'react';
import type { IngestProgress } from '@sflx/shared';
import { accounts as accountsApi } from '../lib/api';

const POLL_MS = 2000;
/** Roughly four minutes of backed-off retries before the poll gives up. */
const MAX_POLL_FAILURES = 12;

function isActive(job: IngestProgress): boolean {
  return job.status === 'queued' || job.status === 'running';
}

export interface IngestProgressState {
  jobs: IngestProgress[];
  active: boolean;
  /** Restart polling, e.g. straight after Refresh enqueues new jobs. */
  refresh: () => void;
}

/**
 * Poll an account's ingest jobs while any of them are queued or running.
 *
 * The loop is a chained timeout rather than an interval: a slow status response
 * under an interval stacks up requests, and ingest is exactly when the Worker
 * is busy. It stops polling as soon as everything has settled, then calls
 * `onSettled` once so the caller can refresh the counts it just changed.
 *
 * A failed read backs off rather than ending the loop: the status endpoint is
 * least reliable exactly while there is something worth watching.
 */
export function useIngestProgress(accountId: string | null, onSettled?: () => void): IngestProgressState {
  const [jobs, setJobs] = useState<IngestProgress[]>([]);
  const [nonce, setNonce] = useState(0);

  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;

  useEffect(() => {
    if (!accountId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let sawActive = false;
    let failures = 0;

    const tick = async () => {
      try {
        const result = await accountsApi.status(accountId);
        if (cancelled) return;
        setJobs(result.jobs);
        failures = 0;

        if (result.jobs.some(isActive)) {
          sawActive = true;
          timer = setTimeout(() => void tick(), POLL_MS);
          return;
        }
        if (sawActive) {
          sawActive = false;
          settledRef.current?.();
        }
      } catch {
        if (cancelled) return;
        // A failed status read is not worth surfacing — the card already shows
        // the last known state — but it must not END the poll. The Worker is
        // busiest exactly while the ingest it is reporting on runs, so a single
        // 500 used to freeze the banner at its last reading for good, and
        // `onSettled` never fired, leaving the counts stale until a manual
        // refresh. Back off instead, and give up only after a long run of them.
        failures += 1;
        if (!sawActive || failures > MAX_POLL_FAILURES) return;
        timer = setTimeout(() => void tick(), POLL_MS * Math.min(failures, 8));
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [accountId, nonce]);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  return { jobs, active: jobs.some(isActive), refresh };
}
